import { Content } from "./types";

export const ARTICLE_TYPES = {
  ANALYSIS: "Analys",
  REPORTAGE: "Reportage",
  CHRONICLE: "Krönika",
  INTERVIEW: "Intervju",
  COMMENT: "Kommentar",
  TELEGRAM: "Telegram",
};

export const CONTENT_TYPE_MAP: { [key: string]: string } = {
  Artikel: "article",
  Playavsnitt: "play-episode",
  Playklipp: "play",
  Poddavsnitt: "podcast-episode",
};

export function bindReferences<T extends Content.Entry>(
  r: Content.Response<T>
): Content.Response<T> {
  const visited = new Map<string, any>();

  const bind: any = (item: T) => {
    // If it's an array, call itself on this item
    if (Array.isArray(item)) {
      return item.map((i) => bind(i)).filter(Boolean);
    }

    // If it's a Link, look up the entry in includes
    if (item?.sys?.type == "Link") {
      if (visited.has(item.sys.id)) {
        return visited.get(item.sys.id); // Return previously processed reference
      }

      const entry = (r.includes as any)?.[item.sys.linkType as any]?.find(
        (i: any) => i.sys.id == item.sys.id
      );
      if (!entry) return null;

      visited.set(item.sys.id, entry); // Mark as visited

      const keys = Object.keys(entry?.fields || {});
      for (const key of keys) {
        (entry.fields as any)[key] = bind((entry?.fields as any)[key]);
      }

      return entry;
    }

    // Else, expect it to be a primary that should stay intact
    return item;
  };

  const items = r.items?.reduce((acc: T[], curr) => {
    const item = { ...curr };
    const keys = Object.keys(item?.fields);
    for (const key of keys) {
      (item.fields as any)[key] = bind((item?.fields as any)[key]);
    }

    acc.push(item);
    return acc;
  }, []);

  return {
    ...r,
    items: items as T[],
  };
}

type RelatedEntry = Content.Article | Content.PodcastEpisode | Content.Play;

export const stripRelations = <T extends RelatedEntry>(entry: T): T => ({
  ...entry,
  fields: {
    ...entry.fields,
    linkedContent: undefined,
    relateratInnehall: undefined,
  },
});

// Contentful resolves linkedContent / relateratInnehall to shared object instances,
// so an article can reference another article that references back — a real cycle
// that breaks JSON.stringify when Next.js serializes RSC -> client boundary payloads.
// This shallow-clones the article and clears nested back-references on each related
// entry, which is all we need for teaser rendering.
export const breakRelationCycles = <T extends Content.Article>(article: T): T => ({
  ...article,
  fields: {
    ...article.fields,
    linkedContent: article.fields.linkedContent?.map(stripRelations),
    relateratInnehall: article.fields.relateratInnehall?.map(stripRelations),
  },
});

export const contentTypeNames = (exlucdeType: string) => {
  return Object.keys(ARTICLE_TYPES)
    .map((type) => (ARTICLE_TYPES as { [key: string]: string })[type] || null)
    .filter((i) => i != exlucdeType);
};
